import {Avatar, Box, IconButton, Stack, Typography} from "@mui/material";
import React, {useCallback, useState} from "react";
import {makeStyles} from "tss-react/mui";
import CloseIcon from "@mui/icons-material/Close";
import type {LearnerAssignedAnnouncementDto} from "@/app2/shared/data/gen";
import {formatRelativeDate} from "@/utils/date-formatters";
import {getAnnouncementAuthorDisplayDate} from "@/app2/pages/ManagerView/Pages/Announcements/announcementAdminStatus";
import {App2AnnouncementModal} from "./App2AnnouncementModal";
import {AnnouncementContentChips} from "./AnnouncementContentChips";
import {hasAnnouncementContentChips} from "./announcementContentIndicators";

interface IProps {
	announcement: LearnerAssignedAnnouncementDto;
	onDismiss?: (id: number) => void;
	onComplete: (slug: string) => void;
	isDismissing?: boolean;
}

const useStyles = makeStyles()((theme) => ({
	itemContent: {
		width: 300,
		height: 207,
		background: "rgba(255, 255, 255, 0.12)",
		border: `1px solid ${theme.palette.primary.main}`,
		flex: "0 0 auto",
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
		boxShadow: theme.shadows[2],
		margin: 4,
		position: "relative",
		cursor: "pointer",
		transition: "all 0.3s ease",
		borderRadius: 24,
		opacity: 1,
		transform: "scale(1)",
		"&:hover": {
			transform: "translateY(-4px) scale(1)",
			boxShadow: theme.shadows[4]
		}
	},
	dismissing: {
		opacity: 0,
		transform: "scale(0.8) !important",
		pointerEvents: "none",
		transition: "opacity 0.3s ease, transform 0.3s ease",
		"&:hover": {
			transform: "scale(0.8) !important"
		}
	},
	cardContents: {
		display: "flex",
		flexDirection: "column",
		height: "100%",
		padding: 16,
		boxSizing: "border-box"
	},
	title: {
		color: theme.palette.text.primary,
		display: "-webkit-box",
		WebkitBoxOrient: "vertical",
		overflow: "hidden",
		fontWeight: 700
	},
	content: {
		color: "rgba(255,255,255,0.8)",
		display: "-webkit-box",
		WebkitBoxOrient: "vertical",
		overflow: "hidden"
	},
	seeMore: {
		color: theme.palette.grey[400],
		cursor: "pointer",
		"&:hover": {
			textDecoration: "underline",
			color: theme.palette.primary.main
		}
	},
	authorAvatar: {
		width: 56,
		height: 56
	}
}));

export const App2AnnouncementCard: React.FC<IProps> = ({
	announcement,
	onDismiss,
	onComplete,
	isDismissing = false
}) => {
	const {classes, cx} = useStyles();
	const [openModal, setOpenModal] = useState(false);

	const handleDismiss = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.stopPropagation();
			onDismiss?.(announcement.id);
		},
		[announcement.id, onDismiss]
	);

	const slug = announcement.slug ?? null;
	const displayDate = getAnnouncementAuthorDisplayDate({
		publishedAt: announcement.publishedAt,
		scheduledDeliveryDate: announcement.scheduledDeliveryDate
	});
	const authorName = `${announcement?.firstname ?? ""} ${announcement?.lastname ?? ""}`;
	const title = announcement.title ?? "";
	const subtitle = announcement.subtitle ?? "";
	const hasContentChips = hasAnnouncementContentChips(announcement);

	return (
		<>
			<Box
				className={cx(classes.itemContent, isDismissing && classes.dismissing)}
				role="article"
				tabIndex={0}
				aria-label={`Announcement card: ${title}`}
				data-test-id={`app2-announcement-card-${announcement.id}`}
				onClick={() => !isDismissing && slug && setOpenModal(true)}
			>
				<Box className={classes.cardContents}>
					<Box display="flex" justifyContent="space-between" alignItems="flex-start">
						<Stack
							direction="row"
							spacing={1}
							alignItems="center"
							marginBottom={1}
							aria-label="Announcement author information"
						>
							<Avatar
								src={announcement?.profileImage?.toString()}
								className={classes.authorAvatar}
								aria-label="Author avatar"
								data-test-id="announcement-author-avatar"
							>
								{announcement?.lastname?.charAt(0) || "?"}
							</Avatar>
							<Stack direction="column" alignItems="flex-start">
								<Typography
									variant="body2"
									className={classes.title}
									sx={{WebkitLineClamp: 1}}
								>
									{authorName}
								</Typography>
								<Typography
									variant="body2"
									className={classes.content}
									data-test-id="announcement-author-title"
								>
									{announcement?.jobTitle ?? ""}
								</Typography>
								<Typography variant="body2" className={classes.content}>
									{formatRelativeDate(displayDate)}
								</Typography>
							</Stack>
						</Stack>

						<IconButton
							color="primary"
							aria-label="Dismiss announcement"
							data-test-id="app2-announcement-dismiss-btn"
							onClick={handleDismiss}
						>
							<CloseIcon />
						</IconButton>
					</Box>

					<AnnouncementContentChips announcement={announcement} />

					<Typography
						variant="body1"
						sx={{WebkitLineClamp: subtitle ? 1 : 2}}
						className={classes.title}
						data-test-id="app2-announcement-card-title"
					>
						{title}
					</Typography>

					<Typography
						variant="body2"
						className={classes.content}
						sx={{
							WebkitLineClamp: hasContentChips ? 1 : 2,
							marginTop: "2px"
						}}
					>
						{subtitle}
					</Typography>

					<Typography
						variant="caption"
						className={classes.seeMore}
						sx={{marginTop: "4px"}}
						aria-label="Open announcement details"
						data-test-id="app2-announcement-see-more"
					>
						See more
					</Typography>
				</Box>
			</Box>

			{openModal && slug && (
				<App2AnnouncementModal
					open={openModal}
					onClose={() => setOpenModal(false)}
					announcement={announcement}
					onComplete={onComplete}
				/>
			)}
		</>
	);
};
